import React, { useMemo } from 'react';
import { formatEuro } from '@doveit/hammer';
import { Spacing, SpacingName } from '@doveit/lego';
import { NextPage } from 'next';
import PageLayout, { PageLayoutSize } from '../client/layouts/page-layout/PageLayout';
import { ALL_BUILDING_SITES_QUERY } from '../client/graphql/queries/building-site';
import PropertyCard from '../client/components/property-card/PropertyCard';
import { getFeatures } from '../client/utils/building-site/buildingSite';
import {
  goToPage, buildBuildingSiteURLObject, buildBuildingSitesURLObject, slugIt,
} from '../client/utils/links';
import { getStructuredData, getBreadcrumbStructuredData } from '../data/structured';
import * as styles from '../client/templates/building-sites/BuildingSites.style';
import PageHead, { PageHeadProps } from '../client/components/page-head/PageHead';
import AppLayout from '../client/layouts/app-layout/AppLayout';
import { BuildingSite } from '../domain/graphcms';
import { BreadcrumbItem } from '../client/components/breadcrumb/Breadcrumb';
import { withError, withGetServerSideError } from '../client/hocs/with-server-side-error/withServerSideError';
import initGraphQLClient from '../client/graphql/initGraphQLClient';
import { importDynamicData } from '../services/importDynamicData';

export interface BuildingSitesProps {
  SEO: PageHeadProps['SEO'],
  buildingSites: BuildingSite[],
}

const getBuildingSiteURLObject = (buildingSite) => buildBuildingSiteURLObject({
  id: buildingSite.id,
  locality: buildingSite.locality.slug,
  slug: slugIt(buildingSite.title),
});

export const BuildingSites: NextPage<BuildingSitesProps> = ({
  SEO,
  buildingSites,
}) => {
  const breadcrumb = useMemo<BreadcrumbItem[]>(() => [
    {
      label: 'Nuove costruzioni',
      urlObject: buildBuildingSitesURLObject(),
    },
  ], []);

  return (
    <AppLayout activePage="building-sites">
      <PageHead
        SEO={SEO}
        structuredData={getStructuredData(getBreadcrumbStructuredData(breadcrumb))}
      />
      <PageLayout>
        <PageLayout.Wrapper maxWidth={PageLayoutSize.L}>
          <PageLayout.Header>
            <h1 css={styles.title}>
              Nuove costruzioni, case e appartamenti nuovi in vendita
            </h1>
          </PageLayout.Header>
          <PageLayout.Content>
            <PageLayout.Main>
              {buildingSites.length === 0 && (
                <div
                  css={styles.noResult}
                  data-ref="no-result-message"
                >
                  <p>
                    Al momento non ci sono nuove costruzioni disponibili
                  </p>
                </div>
              )}
              {buildingSites.length > 0 && (
                <Spacing margin={[SpacingName.MEDIUM, 0]}>
                  <ul>
                    {buildingSites.map((buildingSite) => (
                      <li
                        key={buildingSite.id}
                        css={styles.item}
                      >
                        <PropertyCard
                          title={buildingSite.title}
                          price={`da ${formatEuro(buildingSite.minPrice)}`}
                          features={getFeatures(buildingSite)}
                          description={buildingSite.abstract}
                          image={buildingSite.thumbnail ? buildingSite.thumbnail.image.url : null}
                          urlObject={getBuildingSiteURLObject(buildingSite)}
                          onClick={() => goToPage(getBuildingSiteURLObject(buildingSite))}
                          label="Vedi dettagli immobili"
                          buildingSiteLogo={buildingSite.logo && {
                            src: buildingSite.logo.image.url,
                            alt: buildingSite.logo.imageAlt,
                          }}
                          ariaLabel="Clicca per vedere dettagli immobili"
                          newBuilding
                        />
                      </li>
                    ))}
                  </ul>
                </Spacing>
              )}
              <PageLayout.Breadcrumb items={breadcrumb} />
            </PageLayout.Main>
          </PageLayout.Content>
        </PageLayout.Wrapper>
      </PageLayout>
    </AppLayout>
  );
};

export const getServerSideProps = withGetServerSideError<BuildingSitesProps>(async () => {
  const graphQLClient = initGraphQLClient();

  const [pageData, { data }] = await Promise.all([
    importDynamicData('pages/building-sites.yml'),
    graphQLClient.query({
      query: ALL_BUILDING_SITES_QUERY,
    }),
  ]);

  return {
    props: {
      SEO: {
        ...pageData.SEO,
        robots: data.buildingSites.length === 0
          ? ['noindex', 'nofollow']
          : [],
      },
      buildingSites: data.buildingSites,
    },
  };
});

export default withError(BuildingSites);
